import { Circle, MapPin, Pentagon, RotateCcw, RotateCw, Target } from 'lucide-react';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createGoogleMapsShapeDrawer } from '../../utils/googleMapsShapeDrawer';

// Google Maps API key (same as dashboard)
const GOOGLE_MAPS_API_KEY = 'AIzaSyA2tidsBVB4Kn3Ev4hf84hCsbSCSjFXKsI';

const SiteMapComponent = ({
  center = [20.5937, 78.9629], // Default to India center
  geofenceType = 'Circle',
  coordinates = [],
  radius = 100,
  onCenterChange,
  onGeofenceChange,
  isMainView = false,
  selectedSite = null,
  isEditMode = false,
  isEditSiteMode = false,
  onEditGeofence = null,
  onGeofenceTypeChange = null,
  canUndo = false,
  canRedo = false,
  onUndo = null,
  onRedo = null,
  onMapReady = () => {},
  allSites = [], // All sites with geofences to display on map
  onSiteMarkerClick = null, // Callback when a site marker is clicked
}, ref) => {
  const mapRef = useRef(null);
  const googleMapInstanceRef = useRef(null);
  const centerMarkerRef = useRef(null);
  const geofenceRef = useRef(null);
  const shapeDrawerRef = useRef(null);
  const geocoderRef = useRef(null);
  const placesServiceRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const allSiteMarkersRef = useRef([]); // Store all site markers
  const allSiteGeofencesRef = useRef([]); // Store all site geofences
  const initialLoadFitDoneRef = useRef(false); // Track if initial fit bounds has been done
  // Keep latest callbacks in refs so the map/drawer never calls a stale closure
  const onGeofenceChangeRef = useRef(onGeofenceChange);
  const onCenterChangeRef = useRef(onCenterChange);
  const onMapReadyRef = useRef(onMapReady);

  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [activeDrawMode, setActiveDrawMode] = useState(null);
  const [polygonPointCount, setPolygonPointCount] = useState(0);

  useEffect(() => {
    onGeofenceChangeRef.current = onGeofenceChange;
  }, [onGeofenceChange]);

  useEffect(() => {
    onCenterChangeRef.current = onCenterChange;
  }, [onCenterChange]);

  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  // Expose zoomToSiteGeofence method via ref
  useImperativeHandle(ref, () => ({
    zoomToSiteGeofence: (site) => {
      if (!googleMapInstanceRef.current || !isGoogleMapsLoaded || !site) {
        return;
      }

      const map = googleMapInstanceRef.current;
      const padding = { top: 40, right: 40, bottom: 40, left: 40 };

      if (site.geofenceDetails) {
        if (
          site.geofenceDetails.type === 'Circle' &&
          site.geofenceDetails.center &&
          typeof site.geofenceDetails.radius === 'number'
        ) {
          const cLat = site.geofenceDetails.center.latitude ?? site.geofenceDetails.center.lat;
          const cLng = site.geofenceDetails.center.longitude ?? site.geofenceDetails.center.lng;
          if (typeof cLat === 'number' && typeof cLng === 'number') {
            // Create a temporary circle to get bounds
            const tempCircle = new window.google.maps.Circle({
              center: { lat: cLat, lng: cLng },
              radius: site.geofenceDetails.radius,
            });
            const bounds = tempCircle.getBounds();
            if (bounds) {
              map.fitBounds(bounds, padding);
            } else {
              map.panTo({ lat: cLat, lng: cLng });
              map.setZoom(15);
            }
          }
        } else if (
          site.geofenceDetails.type === 'Polygon' &&
          Array.isArray(site.geofenceDetails.coordinates) &&
          site.geofenceDetails.coordinates.length > 0
        ) {
          const bounds = new window.google.maps.LatLngBounds();
          site.geofenceDetails.coordinates.forEach(coord => {
            const lat = coord.latitude ?? coord.lat ?? (Array.isArray(coord) ? coord[0] : null);
            const lng = coord.longitude ?? coord.lng ?? (Array.isArray(coord) ? coord[1] : null);
            if (typeof lat === 'number' && typeof lng === 'number') {
              bounds.extend(new window.google.maps.LatLng(lat, lng));
            }
          });
          if (!bounds.isEmpty()) {
            map.fitBounds(bounds, padding);
          }
        }
      } else if (site.lat && site.lng) {
        // If no geofence, just zoom to the marker location
        map.panTo({ lat: site.lat, lng: site.lng });
        map.setZoom(15);
      }
    },
  }));

  // Function to verify Google Maps API is fully loaded
  const verifyGoogleMapsLoaded = useCallback(() => {
    return (
      window.google &&
      window.google.maps &&
      window.google.maps.MapTypeId &&
      window.google.maps.ControlPosition
    );
  }, []);

  // Load Google Maps script
  const loadGoogleMapsScript = useCallback(callback => {
    console.log('Loading Google Maps script...');
    if (verifyGoogleMapsLoaded()) {
      console.log('Google Maps already loaded');
      setIsGoogleMapsLoaded(true);
      callback();
      return;
    }

    if (
      document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`)
    ) {
      console.log('Google Maps script already in DOM, waiting for load...');
      const checkGoogleMaps = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          console.log('Google Maps loaded from existing script');
          clearInterval(checkGoogleMaps);
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 100);
      return;
    }

    console.log('Creating new Google Maps script tag');
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('Google Maps script loaded successfully');
      // Wait a bit more to ensure all properties are initialized
      const verifyInterval = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          clearInterval(verifyInterval);
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 50);
      
      // Timeout after 5 seconds if still not loaded
      setTimeout(() => {
        clearInterval(verifyInterval);
        if (verifyGoogleMapsLoaded()) {
          setIsGoogleMapsLoaded(true);
          callback();
        } else {
          console.error('Google Maps API did not fully initialize');
        }
      }, 5000);
    };
    script.onerror = e => {
      console.error('Failed to load Google Maps script.', e);
    };
    document.head.appendChild(script);
  }, [verifyGoogleMapsLoaded]);

  const setMapCursor = useCallback(cursor => {
    if (!googleMapInstanceRef.current) return;
    googleMapInstanceRef.current.setOptions({
      draggableCursor: cursor || null,
      draggingCursor: cursor ? (cursor === 'grab' ? 'grabbing' : cursor) : null,
    });
  }, []);

  // Initialize map
  useEffect(() => {
    loadGoogleMapsScript(() => {
      if (mapRef.current && !googleMapInstanceRef.current) {
        console.log('Initializing Google Map...');
        // Use lower zoom for India default view, higher zoom for specific locations
        const isDefaultIndiaCenter = 
          center[0] === 20.5937 && center[1] === 78.9629;
        const defaultZoom = isDefaultIndiaCenter ? 5 : 15;
        
        const mapOptions = {
          center: { lat: center[0], lng: center[1] },
          zoom: defaultZoom,
          mapTypeId: window.google.maps.MapTypeId?.ROADMAP || 'roadmap',
          zoomControl: true,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition?.RIGHT_TOP || 2,
          },
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          gestureHandling: 'cooperative',
        };

        const map = new window.google.maps.Map(mapRef.current, mapOptions);
        googleMapInstanceRef.current = map;
        console.log('Google Map initialized successfully');

        geocoderRef.current = new window.google.maps.Geocoder();
        if (window.google?.maps?.places) {
          placesServiceRef.current = new window.google.maps.places.PlacesService(map);
          try {
            autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
          } catch (error) {
            console.error('Failed to initialize AutocompleteService', error);
          }
        }

        onMapReadyRef.current?.({
          map,
          google: window.google,
          geocoder: geocoderRef.current,
          placesService: placesServiceRef.current,
          autocompleteService: autocompleteServiceRef.current,
        });

        const emitGeofenceChange = geofenceData => {
          onGeofenceChangeRef.current?.(geofenceData);
        };

        const handleShapeComplete = ({ type, overlay, coordinates, center: circleCenter, radius }) => {
          if (type === 'Polygon') {
            const polygon = overlay;
            const path = polygon.getPath();

            emitGeofenceChange({
              type: 'Polygon',
              coordinates,
            });

            path.addListener('set_at', () => {
              const newCoords = path
                .getArray()
                .map(latLng => [latLng.lat(), latLng.lng()]);
              emitGeofenceChange({
                type: 'Polygon',
                coordinates: newCoords,
              });
            });

            path.addListener('insert_at', () => {
              const newCoords = path
                .getArray()
                .map(latLng => [latLng.lat(), latLng.lng()]);
              emitGeofenceChange({
                type: 'Polygon',
                coordinates: newCoords,
              });
            });

            if (geofenceRef.current) {
              geofenceRef.current.setMap(null);
            }
            geofenceRef.current = polygon;
            setPolygonPointCount(0);
          } else if (type === 'Circle') {
            const circle = overlay;

            emitGeofenceChange({
              type: 'Circle',
              center: circleCenter,
              radius,
            });

            circle.addListener('radius_changed', () => {
              const center = circle.getCenter();
              emitGeofenceChange({
                type: 'Circle',
                center: [center.lat(), center.lng()],
                radius: circle.getRadius(),
              });
            });

            circle.addListener('center_changed', () => {
              const newCenter = circle.getCenter();
              emitGeofenceChange({
                type: 'Circle',
                center: [newCenter.lat(), newCenter.lng()],
                radius: circle.getRadius(),
              });
            });

            if (geofenceRef.current) {
              geofenceRef.current.setMap(null);
            }
            geofenceRef.current = circle;
          }

          setMapCursor('grab');
        };

        if (!isMainView) {
          shapeDrawerRef.current = createGoogleMapsShapeDrawer({
            map,
            google: window.google,
            onModeChange: drawMode => {
              setActiveDrawMode(drawMode);
              setMapCursor(drawMode ? 'crosshair' : 'grab');
              if (drawMode !== 'polygon') {
                setPolygonPointCount(0);
              }
            },
            onProgress: ({ mode: drawMode, pointCount }) => {
              setPolygonPointCount(drawMode === 'polygon' ? pointCount : 0);
            },
            onComplete: handleShapeComplete,
          });
        }

        setMapCursor('grab');

        // Add click listener to set center (disabled while drawing)
        map.addListener('click', event => {
          if (shapeDrawerRef.current?.getMode()) {
            return;
          }

          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          onCenterChangeRef.current?.(lat, lng);
        });

        console.log('Map initialization complete');
      }
    });
  }, [isMainView, loadGoogleMapsScript, setMapCursor]);

  // Update center marker when center changes
  useEffect(() => {
    if (!googleMapInstanceRef.current || !isGoogleMapsLoaded) return;

    // Clear existing center marker
    if (centerMarkerRef.current) {
      centerMarkerRef.current.setMap(null);
      centerMarkerRef.current = null;
    }

    if (
      !center ||
      center.length !== 2 ||
      typeof center[0] !== 'number' ||
      typeof center[1] !== 'number'
    ) {
      return;
    }

    if (center[0] === 0 && center[1] === 0) {
      return;
    }

    const polygonHasCoordinates =
      !selectedSite &&
      geofenceType === 'Polygon' &&
      Array.isArray(coordinates) &&
      coordinates.length >= 3;

    const isDefaultIndiaCenter =
      center[0] === 20.5937 && center[1] === 78.9629;

    if (polygonHasCoordinates && isDefaultIndiaCenter) {
      return;
    }

    const locationPinIcon = {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EF4444"/>
            </svg>
          `),
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 32),
    };

    // Selected site in main/management view
    if (selectedSite && center && center.length === 2 && center[0] !== 0 && center[1] !== 0) {
      centerMarkerRef.current = new window.google.maps.Marker({
        position: { lat: center[0], lng: center[1] },
        map: googleMapInstanceRef.current,
        title: selectedSite.site_name || 'Site Center',
        icon: locationPinIcon,
      });
    } else if (
      !isMainView &&
      !selectedSite &&
      !isDefaultIndiaCenter
    ) {
      // Create/edit site form: show pin for searched or entered coordinates
      centerMarkerRef.current = new window.google.maps.Marker({
        position: { lat: center[0], lng: center[1] },
        map: googleMapInstanceRef.current,
        title: 'Selected location',
        icon: locationPinIcon,
        animation: window.google.maps.Animation?.DROP,
      });
    }

    // Center map on new location and adjust zoom
    googleMapInstanceRef.current.panTo({ lat: center[0], lng: center[1] });
    
    // Adjust zoom based on whether it's default India center or a specific location
    if (isDefaultIndiaCenter && !selectedSite) {
      // Zoom out for India overview
      googleMapInstanceRef.current.setZoom(5);
    } else if (!selectedSite) {
      // Zoom in when user enters specific coordinates
      googleMapInstanceRef.current.setZoom(15);
    }
  }, [center, isGoogleMapsLoaded, selectedSite, geofenceType, coordinates, isMainView]);

  // Update map view to fit the selected site's geofence end-to-end
  useEffect(() => {
    if (!googleMapInstanceRef.current || !isGoogleMapsLoaded || !selectedSite)
      return;

    // Parse geofence details to get center coordinates
    let geofenceData = null;
    if (selectedSite.geofence_details) {
      try {
        geofenceData =
          typeof selectedSite.geofence_details === 'string'
            ? JSON.parse(selectedSite.geofence_details)
            : selectedSite.geofence_details;
      } catch (error) {
        console.error('Error parsing geofence details:', error);
      }
    }

    if (!geofenceData) return;

    const map = googleMapInstanceRef.current;
    const padding = { top: 40, right: 40, bottom: 40, left: 40 };

    if (
      geofenceData.type === 'Circle' &&
      geofenceData.center &&
      typeof geofenceData.radius === 'number'
    ) {
      const cLat = geofenceData.center.latitude ?? geofenceData.center.lat;
      const cLng = geofenceData.center.longitude ?? geofenceData.center.lng;
      if (typeof cLat === 'number' && typeof cLng === 'number') {
        // Create a temporary circle to get bounds
        const tempCircle = new window.google.maps.Circle({
          center: { lat: cLat, lng: cLng },
          radius: geofenceData.radius,
        });
        const bounds = tempCircle.getBounds();
        if (bounds) {
          map.fitBounds(bounds, padding);
        } else {
          map.panTo({ lat: cLat, lng: cLng });
        }
      }
    } else if (
      geofenceData.type === 'Polygon' &&
      Array.isArray(geofenceData.coordinates) &&
      geofenceData.coordinates.length > 0
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      geofenceData.coordinates.forEach(coord => {
        const lat = coord.latitude ?? coord.lat ?? (Array.isArray(coord) ? coord[0] : null);
        const lng = coord.longitude ?? coord.lng ?? (Array.isArray(coord) ? coord[1] : null);
        if (typeof lat === 'number' && typeof lng === 'number') {
          bounds.extend(new window.google.maps.LatLng(lat, lng));
        }
      });
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, padding);
      }
    }
  }, [selectedSite, isGoogleMapsLoaded]);

  // Update geofence display when props change
  useEffect(() => {
    if (!googleMapInstanceRef.current || !isGoogleMapsLoaded) return;

    // Don't wipe an in-progress drawing preview/overlay while the user is still drawing
    if (shapeDrawerRef.current?.getMode()) {
      return;
    }

    // Clear existing geofence
    if (geofenceRef.current) {
      geofenceRef.current.setMap(null);
    }

    // Parse geofence details from selected site
    let geofenceData = null;
    if (selectedSite && selectedSite.geofence_details) {
      try {
        geofenceData =
          typeof selectedSite.geofence_details === 'string'
            ? JSON.parse(selectedSite.geofence_details)
            : selectedSite.geofence_details;
      } catch (error) {
        console.error('Error parsing geofence details:', error);
      }
    }

    // Use geofence data from selected site or fallback to props
    const currentGeofenceType = geofenceData?.type || geofenceType;
    const currentCoordinates = geofenceData?.coordinates || coordinates;
    const currentRadius = geofenceData?.radius || radius;
    const currentCenter = geofenceData?.center || center;

    if (
      currentGeofenceType === 'Circle' &&
      currentCenter &&
      currentRadius > 0
    ) {
      // Support both [lat, lng] and { latitude, longitude }
      const centerLat = Array.isArray(currentCenter)
        ? currentCenter[0]
        : currentCenter.latitude ?? currentCenter.lat;
      const centerLng = Array.isArray(currentCenter)
        ? currentCenter[1]
        : currentCenter.longitude ?? currentCenter.lng;

      if (
        typeof centerLat === 'number' &&
        typeof centerLng === 'number'
      ) {
        const circle = new window.google.maps.Circle({
          strokeColor: '#28a745',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          fillColor: '#28a745',
          fillOpacity: 0.2,
          map: googleMapInstanceRef.current,
          center: { lat: centerLat, lng: centerLng },
          radius: currentRadius,
          editable: isEditMode, // Only editable in edit mode
        });

        // Listen for changes only in edit mode
        if (isEditMode) {
          circle.addListener('radius_changed', () => {
            onGeofenceChangeRef.current?.({
              type: 'Circle',
              center: [centerLat, centerLng],
              radius: circle.getRadius(),
            });
          });

          circle.addListener('center_changed', () => {
            const newCenter = circle.getCenter();
            onGeofenceChangeRef.current?.({
              type: 'Circle',
              center: [newCenter.lat(), newCenter.lng()],
              radius: circle.getRadius(),
            });
          });
        }

        geofenceRef.current = circle;
      }
    } else if (
      currentGeofenceType === 'Polygon' &&
      currentCoordinates &&
      currentCoordinates.length >= 3
    ) {
      // Support both [[lat, lng], ...] and [{ latitude, longitude }, ...]
      const polygonCoords = currentCoordinates.map(coord => ({
        lat: Array.isArray(coord) ? coord[0] : (coord.latitude ?? coord.lat),
        lng: Array.isArray(coord) ? coord[1] : (coord.longitude ?? coord.lng),
      }));

      const polygon = new window.google.maps.Polygon({
        paths: polygonCoords,
        strokeColor: '#28a745',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: '#28a745',
        fillOpacity: 0.2,
        map: googleMapInstanceRef.current,
        editable: isEditMode, // Only editable in edit mode
      });

      // Listen for path changes only in edit mode
      if (isEditMode) {
        const path = polygon.getPath();
        path.addListener('set_at', () => {
          const newCoords = path
            .getArray()
            .map(latLng => [latLng.lat(), latLng.lng()]);
          onGeofenceChangeRef.current?.({
            type: 'Polygon',
            coordinates: newCoords,
          });
        });

        path.addListener('insert_at', () => {
          const newCoords = path
            .getArray()
            .map(latLng => [latLng.lat(), latLng.lng()]);
          onGeofenceChangeRef.current?.({
            type: 'Polygon',
            coordinates: newCoords,
          });
        });

        path.addListener('remove_at', () => {
          const newCoords = path
            .getArray()
            .map(latLng => [latLng.lat(), latLng.lng()]);
          onGeofenceChangeRef.current?.({
            type: 'Polygon',
            coordinates: newCoords,
          });
        });
      }

      geofenceRef.current = polygon;
    }
  }, [
    geofenceType,
    center,
    coordinates,
    radius,
    isGoogleMapsLoaded,
    selectedSite,
    isMainView,
    isEditMode,
  ]);

  // Helper function to create site icon (similar to Dashboard MapComponent)
  const createSiteIcon = useCallback(() => {
    if (!window.google || !window.google.maps) {
      return null;
    }
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EF4444"/>
          </svg>
        `),
      scaledSize: new window.google.maps.Size(32, 32),
      anchor: new window.google.maps.Point(16, 32),
    };
  }, []);

  // Display all sites with geofences on the map (similar to Dashboard MapComponent)
  useEffect(() => {
    const map = googleMapInstanceRef.current;
    if (!map || !isGoogleMapsLoaded || !isMainView) {
      return;
    }

    // Clear existing site markers and geofences
    allSiteMarkersRef.current.forEach(marker => marker.setMap(null));
    allSiteGeofencesRef.current.forEach(geofence => geofence.setMap(null));
    allSiteMarkersRef.current = [];
    allSiteGeofencesRef.current = [];

    // Display all sites with markers and geofences
    allSites.forEach(site => {
      if (
        typeof site.lat !== 'number' ||
        !isFinite(site.lat) ||
        typeof site.lng !== 'number' ||
        !isFinite(site.lng)
      ) {
        return;
      }

      const siteLatLng = { lat: site.lat, lng: site.lng };
      
      // Create marker for site
      const siteIcon = createSiteIcon();
      const marker = new window.google.maps.Marker({
        position: siteLatLng,
        map: map,
        title: site.name,
        icon: siteIcon || undefined, // Use default icon if createSiteIcon returns null
      });

      // Create info window for marker
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="max-width: 300px; overflow: hidden; padding-bottom: 12px; padding-right: 12px;">
            <style>
              .gm-style-iw-c { overflow: hidden !important; }
              .gm-style-iw-d { overflow: hidden !important; max-height: none !important; }
              .gm-style-iw-chr { display: flex !important; justify-content: space-between !important; align-items: center !important; padding: 0 !important; }
            </style>
            <b>${site.name}</b><br>${site.address}
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
        // Call the onSiteMarkerClick callback if provided
        if (onSiteMarkerClick) {
          onSiteMarkerClick(site);
        }
      });

      allSiteMarkersRef.current.push(marker);

      // Add geofence (polygon or circle) if details are available
      if (site.geofenceDetails) {
        if (
          site.geofenceDetails.type === 'Polygon' &&
          site.geofenceDetails.coordinates &&
          site.geofenceDetails.coordinates.length > 0
        ) {
          const polygonCoords = site.geofenceDetails.coordinates.map(
            coord => ({
              lat: coord.latitude,
              lng: coord.longitude,
            })
          );
          const polygon = new window.google.maps.Polygon({
            paths: polygonCoords,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#FF0000',
            fillOpacity: 0.35,
            map: map,
          });
          allSiteGeofencesRef.current.push(polygon);
        } else if (
          site.geofenceDetails.type === 'Circle' &&
          site.geofenceDetails.center &&
          site.geofenceDetails.radius
        ) {
          const circle = new window.google.maps.Circle({
            strokeColor: '#0000FF',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#0000FF',
            fillOpacity: 0.35,
            map: map,
            center: {
              lat: site.geofenceDetails.center.latitude,
              lng: site.geofenceDetails.center.longitude,
            },
            radius: site.geofenceDetails.radius,
          });
          allSiteGeofencesRef.current.push(circle);
        }
      }
    });

    // Fit map to show all sites and geofences when they're first loaded
    if (!initialLoadFitDoneRef.current && allSites.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Extend bounds with all site markers
      allSiteMarkersRef.current.forEach(marker => {
        bounds.extend(marker.getPosition());
      });

      // Extend bounds with all geofences (polygons and circles)
      allSiteGeofencesRef.current.forEach(geofence => {
        if (geofence instanceof window.google.maps.Polygon) {
          const path = geofence.getPath();
          path.forEach(latLng => bounds.extend(latLng));
        } else if (geofence instanceof window.google.maps.Circle) {
          // Extend with circle bounds
          const circleBounds = geofence.getBounds();
          if (circleBounds) {
            const northEast = circleBounds.getNorthEast();
            const southWest = circleBounds.getSouthWest();
            bounds.extend(northEast);
            bounds.extend(southWest);
          }
        }
      });

      if (allSiteMarkersRef.current.length > 0 || allSiteGeofencesRef.current.length > 0) {
        map.fitBounds(bounds);
        console.log('Map fitted to all sites and geofences');
      }
      
      initialLoadFitDoneRef.current = true;
    }
  }, [allSites, isGoogleMapsLoaded, isMainView, createSiteIcon, onSiteMarkerClick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setMap(null);
      }
      if (geofenceRef.current) {
        geofenceRef.current.setMap(null);
      }
      if (shapeDrawerRef.current) {
        shapeDrawerRef.current.destroy();
        shapeDrawerRef.current = null;
      }
      // Cleanup all site markers and geofences
      allSiteMarkersRef.current.forEach(marker => marker.setMap(null));
      allSiteGeofencesRef.current.forEach(geofence => geofence.setMap(null));
      allSiteMarkersRef.current = [];
      allSiteGeofencesRef.current = [];
      geocoderRef.current = null;
      placesServiceRef.current = null;
      autocompleteServiceRef.current = null;
      if (googleMapInstanceRef.current) {
        googleMapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className={`site-map-container ${isMainView ? 'main-view' : ''}`}>
      {!isMainView && (
        <div className="map-instructions">
          <div className="instructions-content">
            <div className="instruction-item">
              <MapPin className="w-4 h-4" />
              <span>
                {activeDrawMode === 'polygon'
                  ? polygonPointCount >= 3
                    ? 'Double-click the map or press Finish Polygon to save'
                    : `Click to add points (${polygonPointCount}/3 minimum)`
                  : activeDrawMode === 'circle'
                    ? 'Click center, then click again to set radius'
                    : 'Use drawing tools to create a geofence, or click the map to set center'}
              </span>
            </div>
          </div>
          <div className="map-action-buttons">
            <button
              type="button"
              className={`map-action-button${activeDrawMode === 'polygon' ? ' active' : ''}`}
              onClick={() => {
                const drawer = shapeDrawerRef.current;
                if (!drawer) return;
                drawer.setMode(activeDrawMode === 'polygon' ? null : 'polygon');
              }}
              disabled={!isGoogleMapsLoaded}
              title="Draw polygon geofence"
            >
              <Pentagon className="w-4 h-4" />
              <span>Polygon</span>
            </button>
            {activeDrawMode === 'polygon' && (
              <button
                type="button"
                className="map-action-button"
                onClick={() => {
                  shapeDrawerRef.current?.finishPolygon();
                }}
                disabled={!isGoogleMapsLoaded || polygonPointCount < 3}
                title={
                  polygonPointCount < 3
                    ? 'Add at least 3 points to finish'
                    : 'Finish and save polygon'
                }
              >
                <Target className="w-4 h-4" />
                <span>Finish Polygon</span>
              </button>
            )}
            <button
              type="button"
              className={`map-action-button${activeDrawMode === 'circle' ? ' active' : ''}`}
              onClick={() => {
                const drawer = shapeDrawerRef.current;
                if (!drawer) return;
                drawer.setMode(activeDrawMode === 'circle' ? null : 'circle');
              }}
              disabled={!isGoogleMapsLoaded}
              title="Draw circle geofence"
            >
              <Circle className="w-4 h-4" />
              <span>Circle</span>
            </button>
          </div>
          {(onUndo || onRedo) && (
            <div className="map-action-buttons">
              {typeof onUndo === 'function' && (
                <button
                  type="button"
                  className="map-action-button"
                  onClick={onUndo}
                  disabled={!canUndo}
                  title="Undo last geofence change"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Undo</span>
                </button>
              )}
              {typeof onRedo === 'function' && (
                <button
                  type="button"
                  className="map-action-button"
                  onClick={onRedo}
                  disabled={!canRedo}
                  title="Redo geofence change"
                >
                  <RotateCw className="w-4 h-4" />
                  <span>Redo</span>
                </button>
              )}
            </div>
          )}
          {/* {onGeofenceTypeChange && (
            <div className="geofence-type-button-container">
              <button
                type="button"
                className="geofence-type-select-btn"
                onClick={() => {
                  // Toggle between Polygon and Circle
                  const newType = geofenceType === 'Polygon' ? 'Circle' : 'Polygon';
                  onGeofenceTypeChange(newType);
                }}
              >
                <span className="geofence-type-label">Geofence Type:</span>
                <span className="geofence-type-value">
                  {geofenceType === 'Polygon' ? (
                    <>
                      <MapPin className="w-4 h-4" />
                      Polygon
                    </>
                  ) : (
                    <>
                      <Target className="w-4 h-4" />
                      Circle
                    </>
                  )}
                </span>
              </button>
            </div>
          )} */}
        </div>
      )}

      {/* Site Info Overlay for Main View - Only show in Edit Site mode */}
      {isMainView && selectedSite && isEditSiteMode && !isEditMode && (
        <div className="site-info-overlay">
          <div className="site-info-card">
            <h3 className="site-info-title">{selectedSite.site_name}</h3>
            <p className="site-info-address">{selectedSite.address}</p>
            <div className="site-info-actions">
              <button className="edit-geofence-btn" onClick={onEditGeofence}>
                <Target className="w-4 h-4" />
                Edit Geofence
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        ref={mapRef}
        className="google-map"
        style={{
          height: isMainView ? '100%' : '600px',
          width: '100%',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      />
    </div>
  );
};

const SiteMap = React.forwardRef(SiteMapComponent);
SiteMap.displayName = 'SiteMap';

export default SiteMap;
